<script lang="ts">
/**
 * Set when the switch was used from the keyboard: the layout changes with the mode, which mounts a new navbar, so the
 * new switch takes the focus back instead of leaving it on `<body>`.
 */
let refocusAfterSwitch = false
</script>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { useI18n } from '@/app/i18n'
import AppIcon from '@/shared/ui/AppIcon.vue'

import { useAiMode } from '../ai-mode'

/**
 * The AI-mode switch of the navbar (docs/ai-mode/wp/AI-1.md): two segments, "Classic" and "AI", as a radio group —
 * Tab reaches the checked one, the arrow keys and Space choose. Icon-only (`compact`, below 992 px) with the names as
 * accessible names and tooltips. The highlight moves at once; the navigation then mounts the other layout.
 */
type Mode = 'classic' | 'ai'

const props = withDefaults(defineProps<{ compact?: boolean }>(), { compact: false })
const { t } = useI18n()
const aiMode = useAiMode()
const root = ref<HTMLElement | null>(null)
const selected = ref<Mode>(aiMode.active.value ? 'ai' : 'classic')

watch(aiMode.active, (active) => {
  selected.value = active ? 'ai' : 'classic'
})

const options = computed(() => [
  { mode: 'classic' as const, label: t('shell.navbar.aiMode.classic'), hint: t('shell.navbar.aiMode.classicHint') },
  { mode: 'ai' as const, label: t('shell.navbar.aiMode.ai'), hint: t('shell.navbar.aiMode.aiHint') },
])

/** The other mode's hint (what choosing it does); the active one is named only when the labels are hidden. */
function tooltipOf(option: { mode: Mode; label: string; hint: string }): string | undefined {
  if (option.mode !== selected.value) return option.hint
  return props.compact ? option.label : undefined
}

/** `fromKeyboard`: an arrow key, or a click with `detail === 0` (Space / Enter on the button). */
async function choose(mode: Mode, fromKeyboard = false): Promise<void> {
  if (mode === selected.value) return
  selected.value = mode
  refocusAfterSwitch = fromKeyboard
  try {
    await (mode === 'ai' ? aiMode.enter() : aiMode.leave())
  } finally {
    // Still mounted (the navigation failed or kept the layout): follow the page again.
    selected.value = aiMode.active.value ? 'ai' : 'classic'
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return
  event.preventDefault()
  const next: Mode = selected.value === 'ai' ? 'classic' : 'ai'
  void choose(next, true)
  void nextTick(() => root.value?.querySelector<HTMLElement>(`[data-mode="${next}"]`)?.focus())
}

onMounted(() => {
  if (!refocusAfterSwitch) return
  refocusAfterSwitch = false
  root.value?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus()
})
</script>

<template>
  <div
    ref="root"
    role="radiogroup"
    :aria-label="t('shell.navbar.aiMode.label')"
    class="ai-mode-switch"
    :class="{ 'is-ai': selected === 'ai', 'is-compact': props.compact }"
    data-testid="ai-mode-switch"
    @keydown="onKeydown"
  >
    <span class="ai-mode-switch__thumb" aria-hidden="true" />
    <button
      v-for="option in options"
      :key="option.mode"
      v-tooltip.bottom="tooltipOf(option)"
      type="button"
      role="radio"
      class="ai-mode-switch__option"
      :class="{ 'is-active': selected === option.mode }"
      :aria-checked="selected === option.mode"
      :aria-label="props.compact ? option.label : undefined"
      :tabindex="selected === option.mode ? 0 : -1"
      :data-mode="option.mode"
      @click="choose(option.mode, $event.detail === 0)"
    >
      <AppIcon v-if="option.mode === 'classic'" name="LayoutIcon" :size="15" />
      <svg
        v-else
        class="ai-mode-switch__sparkle"
        xmlns="http://www.w3.org/2000/svg"
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M10 4q1 6 7 7q-6 1-7 7q-1-6-7-7q6-1 7-7z" />
        <path d="M19 2.5v5M16.5 5h5" />
      </svg>
      <span v-if="!props.compact" class="ai-mode-switch__label">{{ option.label }}</span>
    </button>
  </div>
</template>

<style scoped>
/* The theme switch's size and material (ThemeSwitch): 40 px tall on the navbar's pill fill, a 3 px inset, no rim. */
.ai-mode-switch {
  position: relative;
  display: inline-grid;
  grid-template-columns: 1fr 1fr;
  flex: 0 0 auto;
  height: 40px;
  padding: 3px;
  background: var(--app-fill);
  border: 0;
  border-radius: 20px;
}
.ai-mode-switch__thumb {
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc(50% - 3px);
  background: var(--p-surface-0);
  border-radius: 999px;
  box-shadow: 0 1px 4px rgb(34 41 47 / 14%);
  transition:
    transform 0.25s ease,
    background 0.25s ease;
}
.ai-mode-switch.is-ai .ai-mode-switch__thumb {
  transform: translateX(100%);
  background: var(--p-primary-color);
  box-shadow: 0 2px 8px color-mix(in srgb, var(--p-primary-color) 40%, transparent);
}
.ai-mode-switch__option {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 34px;
  padding: 0 0.85rem;
  color: var(--p-text-muted-color);
  font-size: 0.929rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: color 0.25s ease;
}
.ai-mode-switch.is-compact .ai-mode-switch__option {
  padding: 0 0.55rem;
}
.ai-mode-switch__option:hover:not(.is-active) {
  color: var(--p-text-color);
}
.ai-mode-switch__option.is-active {
  color: var(--p-text-color);
}
.ai-mode-switch.is-ai .ai-mode-switch__option.is-active {
  color: var(--p-primary-contrast-color);
}
.ai-mode-switch__option:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .ai-mode-switch__thumb,
  .ai-mode-switch__option {
    transition: none;
  }
}
</style>
